import { useConfigStore } from "@/store/config";
import {
  BulbOutlined,
  LoginOutlined,
  MenuOutlined,
  MoonOutlined,
  TranslationOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Layout as AntLayout,
  Button,
  Dropdown,
  type MenuProps,
  Segmented,
  Space,
  Switch,
  Typography,
} from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

const { Header, Content } = AntLayout;

export default function LoginLayout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLanguage = useMemo(() => {
    const lang = i18n.language || "zh-CN";
    return lang.startsWith("en") ? "en" : "zh-CN";
  }, [i18n.language]);

  const { theme, setTheme } = useConfigStore();
  const isDark = theme === "dark";
  const isLogin = location.pathname.startsWith("/login");

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("theme", theme);
    }
  }, [theme]);

  const handleLanguageChange = (value: string | number) => {
    const nextLang = value === "en" ? "en" : "zh-CN";
    i18n.changeLanguage(nextLang).then(() => {});
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("lang", nextLang);
    }
  };

  const mobileMenuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "lang-zh-CN",
        icon: <TranslationOutlined />,
        label: `${t("language.chinese")}${currentLanguage === "zh-CN" ? " ✓" : ""}`,
      },
      {
        key: "lang-en",
        icon: <TranslationOutlined />,
        label: `${t("language.english")}${currentLanguage === "en" ? " ✓" : ""}`,
      },
      {
        key: "theme-light",
        icon: <BulbOutlined />,
        label: `${t("common.light")}${!isDark ? " ✓" : ""}`,
      },
      {
        key: "theme-dark",
        icon: <MoonOutlined />,
        label: `${t("common.dark")}${isDark ? " ✓" : ""}`,
      },
      {
        type: "divider",
      },
      {
        key: "route-login",
        icon: <LoginOutlined />,
        label: t("auth.signIn"),
      },
      {
        key: "route-register",
        icon: <UserAddOutlined />,
        label: t("auth.registerTitle"),
      },
    ],
    [currentLanguage, isDark, t],
  );

  const handleMobileMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "lang-en") {
      handleLanguageChange("en");
      return;
    }
    if (key === "lang-zh-CN") {
      handleLanguageChange("zh-CN");
      return;
    }
    if (key === "theme-dark") {
      setTheme("dark");
      return;
    }
    if (key === "theme-light") {
      setTheme("light");
      return;
    }
    if (key === "route-login") {
      navigate("/login");
      return;
    }
    if (key === "route-register") {
      navigate("/register");
    }
  };

  return (
    <AntLayout className="brand-layout-shell">
      <Header className="brand-header sticky top-0 z-10 h-auto px-4 py-3 sm:px-6">
        <div className="flex w-full flex-col gap-3 sm:gap-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="leading-tight">
              <h1 className={isDark ? "text-sky-300" : "text-sky-700"}>
                {t("app.title")}
              </h1>
              <Typography.Text className="block text-xs tracking-[0.35em]">
                {t("app.suite")}
              </Typography.Text>
            </div>
            <div className="hidden sm:block">
              <Space size="middle" wrap>
                <Space size="small">
                  <TranslationOutlined />
                  <Segmented
                    size="small"
                    value={currentLanguage}
                    options={[
                      { label: t("language.chinese"), value: "zh-CN" },
                      { label: t("language.english"), value: "en" },
                    ]}
                    onChange={handleLanguageChange}
                  />
                </Space>
                <Space size="small">
                  <Switch
                    checked={isDark}
                    onChange={(checked) => setTheme(checked ? "dark" : "light")}
                    checkedChildren={<MoonOutlined />}
                    unCheckedChildren={<BulbOutlined />}
                  />
                  <Typography.Text type="secondary">
                    {isDark ? t("common.dark") : t("common.light")}
                  </Typography.Text>
                </Space>
                <Space size="small">
                  <Button
                    type={isLogin ? "primary" : "text"}
                    icon={<LoginOutlined />}
                  >
                    <Link to="/login">{t("auth.signIn")}</Link>
                  </Button>
                  <Button
                    type={!isLogin ? "primary" : "text"}
                    icon={<UserAddOutlined />}
                  >
                    <Link to="/register">{t("auth.registerTitle")}</Link>
                  </Button>
                </Space>
              </Space>
            </div>
            <div className="sm:hidden">
              <Dropdown
                menu={{
                  items: mobileMenuItems,
                  onClick: handleMobileMenuClick,
                }}
                trigger={["click"]}
              >
                <Button icon={<MenuOutlined />}>Menu</Button>
              </Dropdown>
            </div>
          </div>
        </div>
      </Header>
      <Content className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div
            className={`absolute -left-40 top-10 h-80 w-80 rounded-full blur-3xl ${
              isDark ? "bg-sky-500/20" : "bg-sky-300/40"
            }`}
          />
          <div
            className={`absolute -right-24 top-40 h-96 w-96 rounded-full blur-3xl ${
              isDark ? "bg-sky-500/20" : "bg-sky-200/50"
            }`}
          />
        </div>
        <div className="brand-content-shell relative flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center gap-8 px-4 py-8 sm:px-6 sm:py-12 lg:gap-12">
          <div className="hidden w-1/2 flex-col gap-6 lg:flex">
            <Typography.Title
              level={2}
              className={isDark ? "text-white" : "text-slate-900"}
            >
              {t("auth.loginTitle")}
            </Typography.Title>
            <Typography.Paragraph
              className={isDark ? "text-slate-300" : "text-slate-600"}
            >
              {t("auth.loginSubtitle")}
            </Typography.Paragraph>
            <div
              className={
                isDark
                  ? "rounded-2xl border border-white/10 bg-white/5 p-6 text-slate-200"
                  : "rounded-2xl border border-slate-200 bg-white/70 p-6 text-slate-700"
              }
            >
              <Typography.Text>{t("auth.registerNotice")}</Typography.Text>
            </div>
          </div>
          <div className="brand-panel w-full px-4 py-5 sm:px-6 sm:py-6 lg:w-1/2">
            <Outlet context={{ isDark }} />
          </div>
        </div>
      </Content>
    </AntLayout>
  );
}
